// Thin fetch wrapper with auth + JSON for endpoints whose types aren't in
// generated.ts yet (Session 11). Auto-logout on 401 so behaviour matches
// the openapi-fetch middleware in client.ts.
import { useAuthStore } from '@/store/authStore'

const baseUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'
const apiOrigin = baseUrl || window.location.origin

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export class ApiError extends Error {
  status: number
  detail?: unknown
  constructor(status: number, message: string, detail?: unknown) {
    super(message)
    this.status = status
    this.detail = detail
  }
}

export async function apiFetch<T = unknown>(
  method: Method,
  path: string,
  opts: { body?: unknown; query?: Record<string, string | number | undefined | null> } = {}
): Promise<T> {
  const token = useAuthStore.getState().token
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (token) headers.Authorization = `Bearer ${token}`
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json'

  const url = new URL(path, apiOrigin)
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v))
    }
  }

  const res = await fetch(url.toString(), {
    method,
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  })

  if (res.status === 401 && !path.endsWith('/api/auth/me')) {
    useAuthStore.getState().logout({ skipServerCall: true })
  }

  if (!res.ok) {
    let detail: unknown = undefined
    let msg = `${method} ${path} failed (${res.status})`
    try {
      detail = await res.json()
      const d = (detail as { detail?: string })?.detail
      if (typeof d === 'string') msg = d
    } catch {
      /* not json */
    }
    throw new ApiError(res.status, msg, detail)
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

// Binary/document fetch (xlsx pull sheets, printable HTML views). Same auth +
// 401 + error handling as apiFetch but returns the response Blob — callers
// turn it into a download link or an object URL for window.open.
export async function apiFetchBlob(method: Method, path: string, body?: unknown): Promise<Blob> {
  const token = useAuthStore.getState().token
  const headers: Record<string, string> = {}
  if (token) headers.Authorization = `Bearer ${token}`
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const res = await fetch(new URL(path, apiOrigin).toString(), {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  if (res.status === 401) {
    useAuthStore.getState().logout({ skipServerCall: true })
  }

  if (!res.ok) {
    let detail: unknown = undefined
    let msg = `${method} ${path} failed (${res.status})`
    try {
      detail = await res.json()
      const d = (detail as { detail?: string })?.detail
      if (typeof d === 'string') msg = d
    } catch {
      /* not json */
    }
    throw new ApiError(res.status, msg, detail)
  }

  return await res.blob()
}

// Multipart file upload (apiFetch is JSON-only). Content-Type is left unset so
// the browser writes the multipart boundary itself.
export async function apiUpload<T = unknown>(path: string, file: File): Promise<T> {
  const token = useAuthStore.getState().token
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (token) headers.Authorization = `Bearer ${token}`

  const form = new FormData()
  form.append('file', file)

  const res = await fetch(new URL(path, apiOrigin).toString(), {
    method: 'POST',
    headers,
    body: form,
  })

  if (res.status === 401) {
    useAuthStore.getState().logout({ skipServerCall: true })
  }

  if (!res.ok) {
    let detail: unknown = undefined
    let msg = `POST ${path} failed (${res.status})`
    try {
      detail = await res.json()
      const d = (detail as { detail?: string })?.detail
      if (typeof d === 'string') msg = d
    } catch {
      /* not json */
    }
    throw new ApiError(res.status, msg, detail)
  }

  return (await res.json()) as T
}
